import { useRef, useEffect, useState, CSSProperties } from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(false);
  const [regiao, setRegiao] = useState<string>('');

  useEffect(() => {
    // Verificar a região baseada no atributo data-regiao do HTML
    const htmlElement = document.documentElement;
    const regiaoAtual = htmlElement.getAttribute('data-regiao') || '';
    setRegiao(regiaoAtual);
    
    const currentRef = ref.current;
    
    // Verificar tamanho da tela inicialmente
    const checkScreenSize = () => {
      const mobileView = window.innerWidth < 768;
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsMediumScreen(window.innerWidth >= 768 && window.innerWidth < 1024);
      setIsMobileScreen(mobileView);
      
      // Se for mobile, mostrar tudo imediatamente
      if (mobileView) {
        setIsInView(true);
      }
    };
    checkScreenSize();
    
    // Adicionar listener para redimensionamento
    window.addEventListener('resize', checkScreenSize);

    // Configurar o observer para detectar quando o componente está visível
    let observer: IntersectionObserver | null = null;
    
    // Pequeno atraso para evitar problemas de timing
    const timer = setTimeout(() => {
      if (currentRef && !isMobileScreen) {
        // Definir threshold para desktop apenas
        const threshold = 0.3;
        
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            setIsInView(entry.isIntersecting);
          },
          { 
            threshold: threshold,
            rootMargin: '0px'
          }
        );
        
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [isMobileScreen]);

  const sectionStyle: CSSProperties = {
    padding: '3rem 0',
    backgroundColor: '#fff',
    position: 'relative',
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1rem',
  };

  const gridContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isLargeScreen ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)',
    gap: '2rem',
    alignItems: 'center',
    maxWidth: '1140px',
    margin: '0 auto',
  };

  const imageContainerStyle: CSSProperties = {
    position: 'relative',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    height: isLargeScreen ? '100%' : 'auto',
  };

  const imageStyle: CSSProperties = {
    width: '100%',
    height: isLargeScreen ? '100%' : 'auto',
    objectFit: 'cover',
    aspectRatio: isLargeScreen ? 'auto' : '4/3',
  };

  const contentStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.8rem',
  };

  const headingStyle: CSSProperties = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '0.25rem',
    position: 'relative',
  };

  const dividerStyle: CSSProperties = {
    height: '0.25rem',
    width: '5rem',
    backgroundColor: '#ffb03f',
    marginTop: '0.25rem',
    marginBottom: '0.75rem',
  };

  const paragraphStyle: CSSProperties = {
    fontSize: '1.125rem',
    lineHeight: '1.5',
    color: '#4b5563',
    marginBottom: '0.5rem',
  };

  const subheadingStyle: CSSProperties = {
    fontSize: '1.35rem',
    fontWeight: 'bold',
    color: '#4b5563',
    marginTop: '1rem',
    marginBottom: '0.5rem',
  };

  const odsContainerStyle: CSSProperties = {
    marginTop: '3rem',
    marginBottom: isMobileScreen ? '3rem' : '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  };

  const temaStyle: CSSProperties = {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginTop: '1.5rem',
    marginBottom: '0.25rem',
    position: 'relative',
  };

  const smallDividerStyle: CSSProperties = {
    height: '0.25rem',
    width: '4rem',
    backgroundColor: '#ffb03f',
    marginTop: '0.25rem',
    marginBottom: '0.75rem',
  };

  const categoryTextStyle: CSSProperties = {
    fontSize: '1.125rem',
    lineHeight: '1.5',
    color: '#4b5563',
    marginBottom: '0.5rem',
  };

  const boldTextStyle: CSSProperties = {
    fontWeight: 'bold',
  };

  const participationStyle: CSSProperties = {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginTop: '1.5rem',
    marginBottom: '0.25rem',
    position: 'relative',
  };

  const bottomContentStyle: CSSProperties = {
    maxWidth: '1140px',
    margin: '0 auto',
  };

  const categoryBoxContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isLargeScreen ? 
      (regiao === 'Nordeste' ? '1fr' : 
       regiao === 'APS' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)') : 
      (regiao === 'APS' && isMediumScreen ? 'repeat(2, 1fr)' : '1fr'),
    gap: '1.5rem',
    marginTop: '1rem',
    marginBottom: '1.5rem',
  };

  const categoryBoxStyle: CSSProperties = {
    backgroundColor: '#f0f9ff',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    border: '1px solid #e6f0fa',
  };

  const futurosBoxStyle: CSSProperties = {
    ...categoryBoxStyle,
    borderTop: '4px solid #2563eb',
  };

  const titasBoxStyle: CSSProperties = {
    ...categoryBoxStyle,
    borderTop: '4px solid #a31c44',
  };

  const boxTitleStyle: CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '0.75rem',
    display: 'flex',
    alignItems: 'center',
  };

  const titasTitleStyle: CSSProperties = {
    ...boxTitleStyle,
    color: '#a31c44',
  };

  const participationBoxStyle: CSSProperties = {
    backgroundColor: '#f8fafc',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginTop: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
    borderLeft: '4px solid #64748b',
  };

  const boxIconStyle: CSSProperties = {
    backgroundColor: '#dbeafe',
    borderRadius: '50%',
    padding: '0.5rem',
    marginRight: '0.75rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const odsTagStyle: CSSProperties = {
    display: 'inline-block',
    backgroundColor: '#2563eb',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    fontWeight: 'bold',
    marginLeft: '0.5rem',
  };

  const orangeOdsTagStyle: CSSProperties = {
    ...odsTagStyle,
    backgroundColor: '#ffb03f',
  };

  const titanOdsTagStyle: CSSProperties = {
    ...odsTagStyle,
    backgroundColor: '#a31c44',
  };

  return (
    <section id="about" style={sectionStyle} ref={ref}>
      <div style={containerStyle}>
        <div style={gridContainerStyle}>
          <motion.div
            initial={isMobileScreen ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: isMobileScreen ? 0 : 0.5, delay: isMobileScreen ? 0 : 0.2 }}
            style={imageContainerStyle}
          >
            <img
              src="/images/students.jpg"
              alt="Estudantes participando do hackathon"
              style={{
                ...imageStyle,
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
          </motion.div>
          
          <motion.div
            initial={isMobileScreen ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: isMobileScreen ? 0 : 0.5, delay: isMobileScreen ? 0 : 0.3 }}
            style={contentStyle}
          >
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              fontFamily: 'Kobe, sans-serif',
              color: '#1e3a8a'
            }} className="title-font">
              O que é um Hackathon?
            </h2>
            <div style={dividerStyle}></div>
            
            <p style={paragraphStyle}>
              É uma maratona de programação onde crianças e adolescentes se reúnem para criar uma solução para um problema proposto. O processo envolve a ideação, a criação de um protótipo da solução e a apresentação do projeto.
            </p>
            
            <h3 style={subheadingStyle}>QUAL SERÁ O DESAFIO?</h3>
            
            <p style={paragraphStyle}>
              A ideia é que os alunos desenvolvam um aplicativo que ofereça uma solução inovadora para os problemas apresentados. Essa solução deverá ser construída em 3 etapas: 1ª Preenchimento do CANVAS, 2ª preenchimento do STORYBOARD e 3ª gravação do PITCH.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={isMobileScreen ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobileScreen ? 0 : 0.5, delay: isMobileScreen ? 0 : 0.4 }}
          style={odsContainerStyle}
        >
          <img 
            src="/images/faixa-ods.png" 
            alt="Empresa comprometida com os 17 objetivos de desenvolvimento sustentável da ONU" 
            style={{ 
              maxWidth: isMobileScreen ? '80%' : '30%', 
              height: 'auto' 
            }} 
          />
        </motion.div>

        <motion.div
          initial={isMobileScreen ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobileScreen ? 0 : 0.5, delay: isMobileScreen ? 0 : 0.5 }}
          style={bottomContentStyle}
        >
          <h3 style={{
            ...temaStyle, 
            textTransform: 'none',
            fontFamily: 'Kobe, sans-serif'
          }}><strong>{regiao === 'Nordeste' ? 'Categoria e tema' : 'Categorias e temas'}</strong></h3>
          <div style={smallDividerStyle}></div>
          <p style={paragraphStyle}>
            {regiao === 'Nordeste' 
              ? ''
              : regiao === 'APS'
                ? 'O tema do Hackathon será apresentado para três categorias:'
                : 'O tema do Hackathon será diferente para duas categorias:'}
          </p>
          
          <div style={categoryBoxContainerStyle}>
            {/* Card FUTUROS INOVADORES (5º e 6º ANO) para APS */}
            {(regiao === 'APS') && (
            <motion.div 
              style={futurosBoxStyle}
              whileHover={{ transform: 'translateY(-5px)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
                <img src="/images/ODS16.jpg" alt="ODS 16" style={{height: '5rem', borderRadius: '4px'}} />
                <div style={{flex: 1}}>
                  <div style={boxTitleStyle}>
                    <div>
                      FUTUROS INOVADORES
                      <div style={{fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.25rem'}}>
                        5º ANO e 6º ANO
                      </div>
                    </div>
                  </div>
                  <p style={{...categoryTextStyle, marginBottom: '0.75rem'}}>
                    Tema: <span style={boldTextStyle}>"Combate ao Bullying"</span>
                  </p>
                </div>
              </div>
            </motion.div>
            )}
            
            {/* Card FUTUROS INOVADORES (7º e 8º ANO) para APS */}
            {(regiao === 'APS') && (
            <motion.div 
              style={futurosBoxStyle}
              whileHover={{ transform: 'translateY(-5px)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
                <img src="/images/ODS16.jpg" alt="ODS 16" style={{height: '5rem', borderRadius: '4px'}} />
                <div style={{flex: 1}}>
                  <div style={boxTitleStyle}>
                    <div>
                      FUTUROS INOVADORES
                      <div style={{fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.25rem'}}>
                        7º ANO e 8º ANO
                      </div>
                    </div>
                  </div>
                  <p style={{...categoryTextStyle, marginBottom: '0.75rem'}}>
                    Tema: <span style={boldTextStyle}>"Combate ao Bullying"</span>
                  </p>
                </div>
              </div>
            </motion.div>
            )}
            
            {/* Card FUTUROS INOVADORES original para regiões que não são APS nem NORDESTE */}
            {(regiao !== 'Nordeste' && regiao !== 'APS') && (
            <motion.div 
              style={futurosBoxStyle}
              whileHover={{ transform: 'translateY(-5px)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
                <img src="/images/ODS16.jpg" alt="ODS 16" style={{height: '5rem', borderRadius: '4px'}} />
                <div style={{flex: 1}}>
                  <div style={boxTitleStyle}>
                    <div>
                      FUTUROS INOVADORES
                      <div style={{fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.25rem'}}>
                        5º ANO, 6º ANO, 7º ANO e 8º ANO
                      </div>
                    </div>
                  </div>
                  <p style={{...categoryTextStyle, marginBottom: '0.75rem'}}>
                    Tema: <span style={boldTextStyle}>"Combate ao Bullying"</span>
                  </p>
                </div>
              </div>
            </motion.div>
            )}
            
            {/* Card FUTUROS INOVADORES específico para NORDESTE */}
            {(regiao === 'Nordeste') && (
            <motion.div 
              style={{
                ...futurosBoxStyle,
                width: '100%'
              }}
              whileHover={{ transform: 'translateY(-5px)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
                <img src="/images/ODS16.jpg" alt="ODS 16" style={{height: '5rem', borderRadius: '4px'}} />
                <div style={{flex: 1}}>
                  <div style={boxTitleStyle}>
                    <div>
                      FUTUROS INOVADORES
                      <div style={{fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.25rem'}}>
                        6º ANO, 7º ANO, 8º ANO e 9º ANO
                      </div>
                    </div>
                  </div>
                  <p style={{...categoryTextStyle, marginBottom: '0.75rem'}}>
                    Tema: <span style={boldTextStyle}>"Combate ao Bullying"</span>
                  </p>
                </div>
              </div>
            </motion.div>
            )}
            
            {/* Card TITÃS DA TECNOLOGIA para todas as regiões exceto NORDESTE */}
            {(regiao !== 'Nordeste') && (
            <motion.div 
              style={titasBoxStyle}
              whileHover={{ transform: 'translateY(-5px)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
                <img src="/images/ODS8.png" alt="ODS 8" style={{height: '5rem', borderRadius: '4px'}} />
                <div style={{flex: 1}}>
                  <div style={titasTitleStyle}>
                    <div>
                      TITÃS DA TECNOLOGIA
                      <div style={{fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.25rem'}}>
                        9º ANO (FUND II), 1ª SÉRIE, 2ª SÉRIE E 3ª SÉRIE (ENSINO MÉDIO)
                      </div>
                    </div>
                  </div>
                  <p style={{...categoryTextStyle, marginBottom: '0.75rem'}}>
                    Tema: <span style={boldTextStyle}>"Como utilizar a IA para transformar o mercado de trabalho"</span>
                  </p>
                </div>
              </div>
            </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About; 